<template>
  <div class="shell-container">
    <!-- Phase: Reveal -->
    <div v-if="phase === 'idle'" class="overlay">
      <div class="text-5xl mb-4">🏆</div>
      <h2 class="text-2xl font-bold text-white mb-2">Hütchenspiel</h2>
      <p class="text-white/60 text-sm mb-1">Unter einem Becher versteckt sich ein Rabatt!</p>
      <p class="text-white/40 text-xs mb-6">Beobachte genau, wie die Becher gemischt werden.</p>
      <button @click="startGame" class="start-btn" :disabled="loading">
        <span v-if="loading" class="animate-spin mr-2">⏳</span>
        Spiel starten
      </button>
    </div>

    <!-- Game Area -->
    <div v-if="phase !== 'idle'" class="game-area">
      <!-- Status -->
      <div class="status-bar">
        <span class="text-white/40 text-xs uppercase tracking-wider">
          {{ phaseText }}
        </span>
        <span v-if="phase === 'shuffling'" class="text-yellow-400 text-xs font-bold">
          Runde {{ currentMove + 1 }} / {{ totalMoves }}
        </span>
      </div>

      <!-- Cups -->
      <div class="cups-area" ref="cupsArea">
        <div
          v-for="(cup, i) in cups"
          :key="i"
          class="cup-wrapper"
          :style="getCupStyle(i)"
          @click="guessCup(i)"
        >
          <!-- Cup body -->
          <div
            class="cup"
            :class="{
              'cup-lifted': cup.lifted,
              'cup-clickable': phase === 'guessing',
              'cup-correct': phase === 'result' && i === correctCup && guessedCorrectly,
              'cup-wrong': phase === 'result' && i === guessedCup && !guessedCorrectly,
              'cup-reveal': phase === 'result' && i === correctCup,
            }"
          >
            <div class="cup-body">
              <div class="cup-rim"></div>
              <div class="cup-side"></div>
              <div class="cup-bottom"></div>
              <div class="cup-shine"></div>
            </div>
          </div>

          <!-- Hidden item (discount tag) -->
          <div
            class="hidden-item"
            :class="{ 'item-visible': (phase === 'reveal' && i === hiddenCup) || (phase === 'result' && i === correctCup) }"
          >
            <div class="discount-tag">
              <span class="tag-percent">%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Guessing prompt -->
      <div v-if="phase === 'guessing'" class="guess-prompt">
        <p class="text-white font-bold text-lg">Welcher Becher?</p>
        <p class="text-white/40 text-sm">Tippe auf den Becher mit dem Rabatt!</p>
      </div>

      <!-- Result -->
      <div v-if="phase === 'result'" class="result-area">
        <div v-if="guessedCorrectly" class="result-win">
          <div class="confetti-burst"></div>
          <div class="text-4xl mb-2">🎉</div>
          <p class="text-green-400 font-bold text-xl">Richtig!</p>
          <p v-if="reward" class="text-yellow-400 font-bold mt-2">{{ reward.message }}</p>
        </div>
        <div v-else class="result-lose">
          <div class="text-4xl mb-2">😅</div>
          <p class="text-red-400 font-bold text-xl">Leider falsch!</p>
          <p class="text-white/40 text-sm mt-1">Der Rabatt war unter Becher {{ correctCup + 1 }}.</p>
        </div>
        <button @click="resetGame" class="retry-btn mt-6">Nochmal spielen</button>
        <button @click="$emit('close')" class="close-text mt-2">Schließen</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { gamificationService } from '@/services/api/gamificationService'

const emit = defineEmits(['close', 'apply-discount'])

const phase = ref('idle')
const loading = ref(false)
const hiddenCup = ref(0)
const correctCup = ref(0)
const guessedCup = ref(-1)
const guessedCorrectly = ref(false)
const reward = ref(null)
const currentMove = ref(0)
const totalMoves = ref(10)

let gameToken = ''
let moves = []

const cups = ref([
  { x: 0, lifted: false },
  { x: 1, lifted: false },
  { x: 2, lifted: false },
])

// Track logical positions: positions[i] = visual slot of cup i
const positions = ref([0, 1, 2])

const phaseText = computed(() => {
  const texts = {
    reveal: 'Merke dir den Becher...',
    shuffling: 'Die Becher werden gemischt!',
    guessing: 'Wo ist der Rabatt?',
    result: guessedCorrectly.value ? 'Gewonnen!' : 'Verloren...',
  }
  return texts[phase.value] || ''
})

function getCupStyle(i) {
  const slot = positions.value[i]
  const spacing = 80
  const offset = slot * spacing
  return {
    transform: `translateX(${offset}px)`,
    transition: phase.value === 'shuffling' ? 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)' : 'transform 0.5s ease',
    zIndex: cups.value[i].lifted ? 10 : 1,
  }
}

async function startGame() {
  loading.value = true
  try {
    const { data } = await gamificationService.shellGameSetup()
    gameToken = data.token
    hiddenCup.value = data.hiddenCup
    correctCup.value = data.hiddenCup
    moves = data.moves || []
    totalMoves.value = moves.length
    currentMove.value = 0
  } catch (e) {
    // Fallback
    gameToken = ''
    hiddenCup.value = Math.floor(Math.random() * 3)
    correctCup.value = hiddenCup.value
    moves = generateFallbackMoves()
    totalMoves.value = moves.length
  } finally {
    loading.value = false
  }

  positions.value = [0, 1, 2]
  guessedCup.value = -1
  guessedCorrectly.value = false
  reward.value = null

  // Reveal phase
  phase.value = 'reveal'
  cups.value[hiddenCup.value].lifted = true
  await sleep(1800)
  cups.value[hiddenCup.value].lifted = false
  await sleep(600)

  // Shuffle phase
  phase.value = 'shuffling'
  await executeShuffle()

  // Guessing phase
  phase.value = 'guessing'
}

function generateFallbackMoves() {
  const m = []
  for (let i = 0; i < 10; i++) {
    let a = Math.floor(Math.random() * 3)
    let b = Math.floor(Math.random() * 3)
    while (b === a) b = Math.floor(Math.random() * 3)
    m.push({ from: a, to: b, speed: Math.max(200, 550 - i * 30) })
  }
  return m
}

async function executeShuffle() {
  for (let i = 0; i < moves.length; i++) {
    currentMove.value = i
    const move = moves[i]

    // Find which cups are at the "from" and "to" slots
    const cupAtFrom = positions.value.indexOf(move.from)
    const cupAtTo = positions.value.indexOf(move.to)

    if (cupAtFrom !== -1 && cupAtTo !== -1) {
      // Swap
      const temp = positions.value[cupAtFrom]
      positions.value[cupAtFrom] = positions.value[cupAtTo]
      positions.value[cupAtTo] = temp

      // Track where the hidden cup went
      if (cupAtFrom === hiddenCup.value) {
        // nothing to track - hiddenCup is the index of the cup itself
      }
    }

    await sleep(Math.max(150, move.speed || 400))
  }

  // After shuffle, correctCup = find where hiddenCup ended up visually
  // But for guessing, we need to find which cup INDEX has the prize
  // The prize follows cup index = hiddenCup (from server)
  // The visual slot of that cup = positions.value[hiddenCup.value]
}

async function guessCup(cupIndex) {
  if (phase.value !== 'guessing') return
  guessedCup.value = cupIndex

  if (gameToken) {
    try {
      const { data } = await gamificationService.shellGameGuess({
        token: gameToken,
        guess: cupIndex,
      })
      guessedCorrectly.value = data.correct
      correctCup.value = hiddenCup.value
      reward.value = data.reward
      if (data.correct && data.reward) emit('apply-discount', { ...data.reward, gameName: 'Hütchenspiel' })
    } catch (e) {
      guessedCorrectly.value = cupIndex === hiddenCup.value
      correctCup.value = hiddenCup.value
    }
  } else {
    guessedCorrectly.value = cupIndex === hiddenCup.value
    correctCup.value = hiddenCup.value
  }

  if (guessedCorrectly.value && navigator.vibrate) navigator.vibrate([50, 30, 50])
  phase.value = 'result'
}

function resetGame() {
  phase.value = 'idle'
  positions.value = [0, 1, 2]
  cups.value.forEach(c => { c.lifted = false })
}

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms))
}
</script>

<style scoped>
.shell-container {
  @apply relative w-full min-h-[420px] bg-[#0a0a14] overflow-hidden rounded-2xl;
}
.overlay {
  @apply absolute inset-0 flex flex-col items-center justify-center z-20;
  background: rgba(10,10,20,0.92);
  backdrop-filter: blur(8px);
}
.game-area {
  @apply flex flex-col items-center py-6 px-4;
}
.status-bar {
  @apply flex items-center justify-between w-full mb-8 px-2;
}
.cups-area {
  @apply relative flex justify-center;
  height: 160px;
  width: 240px;
}
.cup-wrapper {
  @apply absolute;
  width: 60px;
  left: 6px;
}
.cup {
  @apply relative transition-transform duration-300;
  width: 60px;
  height: 80px;
}
.cup-body {
  @apply relative w-full h-full;
}
.cup-rim {
  @apply absolute top-0 left-1/2 -translate-x-1/2;
  width: 56px;
  height: 10px;
  background: linear-gradient(180deg, #8B7355, #6B5B3E);
  border-radius: 4px 4px 0 0;
  box-shadow: 0 -2px 6px rgba(139,115,85,0.3);
}
.cup-side {
  @apply absolute top-[8px] left-1/2 -translate-x-1/2;
  width: 52px;
  height: 64px;
  background: linear-gradient(180deg, #7B6548, #5C4832);
  clip-path: polygon(5% 0%, 95% 0%, 85% 100%, 15% 100%);
  box-shadow: inset -3px 0 8px rgba(0,0,0,0.3), inset 3px 0 8px rgba(255,255,255,0.05);
}
.cup-bottom {
  @apply absolute bottom-0 left-1/2 -translate-x-1/2;
  width: 44px;
  height: 6px;
  background: #4A3828;
  border-radius: 0 0 2px 2px;
}
.cup-shine {
  @apply absolute top-[12px] left-[18px];
  width: 8px;
  height: 40px;
  background: linear-gradient(180deg, rgba(255,255,255,0.08), transparent);
  border-radius: 4px;
}

.cup-lifted {
  animation: liftCup 0.5s ease forwards;
}
.cup-clickable .cup-body {
  cursor: pointer;
}
.cup-clickable:hover .cup-body {
  transform: translateY(-4px);
  filter: brightness(1.2);
}
.cup-correct .cup-body {
  filter: brightness(1.3);
  animation: liftCup 0.5s ease forwards;
}
.cup-wrong .cup-body {
  filter: brightness(0.6);
}
.cup-reveal .cup-body {
  animation: liftCup 0.5s ease forwards;
}

.hidden-item {
  @apply absolute bottom-0 left-1/2 -translate-x-1/2;
  @apply opacity-0 transition-opacity duration-500;
}
.item-visible {
  @apply opacity-100;
}
.discount-tag {
  @apply w-10 h-10 rounded-full flex items-center justify-center;
  background: linear-gradient(135deg, #FFD700, #FFA500);
  box-shadow: 0 0 15px rgba(255,215,0,0.5);
  animation: tagGlow 1s ease infinite alternate;
}
.tag-percent {
  @apply text-lg font-black text-gray-900;
}

.guess-prompt {
  @apply text-center mt-8;
  animation: fadeIn 0.5s ease;
}
.result-area {
  @apply text-center mt-8;
  animation: fadeIn 0.5s ease;
}
.result-win { @apply relative; }
.confetti-burst {
  @apply absolute -top-8 left-1/2 -translate-x-1/2 w-32 h-32 pointer-events-none;
  background: radial-gradient(circle, rgba(255,215,0,0.3) 0%, transparent 70%);
  animation: burstFade 1s ease forwards;
}

.start-btn {
  @apply px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-xl;
  @apply hover:from-amber-400 hover:to-orange-500 transition-all duration-200 active:scale-95;
}
.start-btn:disabled { @apply opacity-50 cursor-not-allowed; }
.retry-btn {
  @apply px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold rounded-xl;
  @apply hover:from-purple-500 hover:to-indigo-500 transition-all duration-200 active:scale-95;
}
.close-text {
  @apply text-white/40 text-sm hover:text-white/60 transition-colors;
}

@keyframes liftCup {
  0% { transform: translateY(0); }
  50% { transform: translateY(-50px); }
  100% { transform: translateY(-50px); }
}
@keyframes tagGlow {
  to { box-shadow: 0 0 25px rgba(255,215,0,0.8); }
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes burstFade {
  0% { transform: translate(-50%) scale(0); opacity: 1; }
  100% { transform: translate(-50%) scale(3); opacity: 0; }
}
</style>
